import { useCallback, useEffect, useState } from "react";
import { PlaylistItemData } from "../playlist/data/PlaylistItemData";
import { ItemId } from "../playlist/data/PlaylistTypes";
import { CarouselSlide } from "./slide/CarouselSlide";

interface PageInItem {
  itemId: ItemId;
  pageInItem: number;
}

export interface CarouselPosition {
  activeSlideIndex: number;
  activeItem: PlaylistItemData | undefined;
  pageInItem: number;
  activeLyricsPart: number | undefined;
  setActiveLyricsPart: (
    value: React.SetStateAction<number | undefined>,
  ) => void;
  goToItem: (
    item: PlaylistItemData | undefined,
    pageInItem?: number,
    lyricsPart?: number,
  ) => void;
  goToSlide: (slideIndex: number) => void;
}

/**
 * The selected item is owned by the caller (shared with the playlist editor), the page within the
 * item and the lyrics part are owned here. The active slide index is always derived from them.
 */
export function useCarouselPosition(
  slides: readonly CarouselSlide[],
  selectedItem: PlaylistItemData | undefined,
  setSelectedItem: (item: PlaylistItemData | undefined) => void,
): CarouselPosition {
  //the page is only valid for the item it was set for, selecting another item starts at its first page
  const [pageInItemState, setPageInItemState] = useState<
    PageInItem | undefined
  >(undefined);
  const [activeLyricsPart, setActiveLyricsPart] = useState<number | undefined>(
    undefined,
  );

  const requestedPageInItem =
    pageInItemState && pageInItemState.itemId === selectedItem?.id
      ? pageInItemState.pageInItem
      : 0;
  const activeSlideIndex = findSlideIndex(
    slides,
    selectedItem?.id,
    requestedPageInItem,
  );
  const activeItem = slides[activeSlideIndex]?.item;
  const pageInItem = slides[activeSlideIndex]?.pageInItem ?? 0;

  //opening the carousel without a (still existing) selected item selects the first one
  useEffect(() => {
    if (activeItem !== undefined && activeItem.id !== selectedItem?.id)
      setSelectedItem(activeItem);
  }, [activeItem, selectedItem, setSelectedItem]);

  const goToItem = useCallback(
    (
      item: PlaylistItemData | undefined,
      pageInItem: number = 0,
      lyricsPart?: number,
    ) => {
      setActiveLyricsPart(lyricsPart);
      if (item !== undefined)
        setPageInItemState({ itemId: item.id, pageInItem });
      setSelectedItem(item);
    },
    [setSelectedItem],
  );

  function goToSlide(slideIndex: number) {
    const slide = slides[slideIndex];
    if (slide !== undefined) goToItem(slide.item, slide.pageInItem);
  }

  return {
    activeSlideIndex,
    activeItem,
    pageInItem,
    activeLyricsPart,
    setActiveLyricsPart,
    goToItem,
    goToSlide,
  };
}

//the slide of the given page of the item, falling back to the item's first slide if the page does not exist
//(e.g. in lyrics mode) and to the first slide if the item does not exist
function findSlideIndex(
  slides: readonly CarouselSlide[],
  itemId: ItemId | undefined,
  pageInItem: number,
): number {
  const firstSlideIndex = slides.findIndex((slide) => slide.item.id === itemId);
  if (firstSlideIndex === -1) return 0;
  const slideIndex = firstSlideIndex + pageInItem;
  return slides[slideIndex]?.item.id === itemId ? slideIndex : firstSlideIndex;
}
